// CHA-582 / W1 — Work list (platform component).
//
// Renders findings grouped by state, OVERDUE FIRST (Ron's ruling). Platform-shaped: it takes a
// plain `findings` array and an optional `onSelect`; it does NOT read Firestore and has no tenant
// coupling. The InMocean shell mounts it at #/work in W1b. Uses the Carbon Tailwind class grammar
// (font-plex-mono, carbon-* tokens) — not a new table grammar.
//
// In W1 only findings exist, so the visible groups are Open / not sent, snoozed, closed. In W2b
// runs join the SAME queue tagged by kind ('run'): a started run appears in the "Run · open" group
// while its finding stays in "Open / not sent". Send starts a run from a finding (see onSend).

import React from 'react';
import { groupFindings, stateKey } from '../workLayer/groupFindings';

const SEV = (f) => {
  const days = Number(f?.evidence?.days_past_cancel);
  const gp = Number(f?.evidence?.gp_pct);
  if ((Number.isFinite(days) && days >= 14) || (Number.isFinite(gp) && gp < 10)) return 'bg-carbon-red-60';
  return 'bg-carbon-gray-50';
};
const money = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `$${Math.round(v)}`;
};

// Actions are passed in as optional callbacks so this platform component stays TENANT-AGNOSTIC
// (CHA-588): the tenant shell owns the gateway call and the tenantId. A callback rendered here is
// just a button; WorkList never knows which tenant it acts on. Reopen is only offered on findings
// whose state can move back to open (closed / snoozed) — mirrors the FINDING_TRANSITIONS gate.
const CAN_REOPEN = new Set(['closed', 'snoozed']);

const isRun = (item) => item?.kind === 'run';

// `busy` disables the action buttons while the shell has a gateway call in flight, so a
// double-click can't submit the same action twice. It stays tenant-agnostic — just a boolean.
// CHA-592 / W2b — `onSend` starts a workflow run from a finding. It is offered ONLY on findings
// (not runs) that have no `runId` yet, mirroring the gateway's one-run-per-finding guard. WorkList
// stays tenant-agnostic: it knows nothing of the definitionId or tenantId — the shell owns both.
//
// CHA-597 — `canSend(finding)` is the tenant's eligibility predicate: does a workflow definition
// actually BIND this finding's rule? WorkList must not answer that itself — knowing which rules
// have definitions would make this component tenant-aware, which is the one thing it is not. The
// shell decides; this asks.
//
// The default is FAIL-CLOSED on purpose. A caller that passes `onSend` but forgets `canSend` gets
// NO Send button rather than a Send on everything — "Send on everything" is precisely the defect
// this ticket exists to remove (run_a59a44ec: a Send on a GP-drift finding posted the past-cancel
// definition and composed past-cancel-worded mail about a margin-drift PO). A missing predicate is
// a wiring mistake; the safe reading of a wiring mistake is "not eligible".
export default function WorkList({ findings = [], onSelect, onReopen, onAssign, onSetLabels, onSend, canSend = () => false, busy = false }) {
  const groups = groupFindings(findings);
  // Count via stateKey so a padded/unknown state is bucketed the same way groupFindings buckets it
  // (both fall through to 'open') — the header total then matches the Open group's count exactly.
  const totalOpen = findings.filter((f) => stateKey(f) === 'open').length;

  return (
    <div className="space-y-4" data-testid="work-list">
      <div className="font-plex-mono text-[11px] uppercase tracking-[0.14em] text-carbon-gray-50">
        Work · {findings.length} items · {totalOpen} open
      </div>

      {groups.length === 0 && (
        <div className="border border-carbon-border bg-carbon-white px-5 py-6 text-sm text-carbon-gray-60">
          No open findings.
        </div>
      )}

      {groups.map((group) => (
        <section key={group.key} className="border border-carbon-border bg-carbon-white" data-testid={`group-${group.key}`}>
          <div className="flex items-center justify-between border-b border-carbon-border bg-carbon-gray-10 px-4 py-2">
            <span className="font-plex-mono text-[10px] uppercase tracking-[0.12em] text-carbon-gray-60">
              {group.label} · {group.count}
            </span>
            <span className="font-plex-mono text-[11px] text-carbon-gray-70">{money(group.exposure)}</span>
          </div>
          <ul className="divide-y divide-carbon-border">
            {group.items.map((f) => {
              const run = isRun(f);
              // Finding-only actions: a run is not a finding, so assign/labels/reopen/send never
              // render on a run row (they act on tickets via the gateway). Reopen mirrors the
              // FINDING_TRANSITIONS gate; Send mirrors the gateway's one-run guard (no existing runId).
              const showReopen = !run && onReopen && CAN_REOPEN.has(stateKey(f));
              // CHA-597 — …and only when a definition binds this finding's rule (tenant's call).
              const showSend = !run && onSend && !f.runId && canSend(f);
              const hasActions = !run && (onAssign || onSetLabels || showReopen || showSend);
              return (
                <li key={f.id} className="flex items-stretch">
                  <button
                    type="button"
                    onClick={() => onSelect && onSelect(f)}
                    className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left hover:bg-carbon-gray-10"
                  >
                    <span className={`h-8 w-[3px] shrink-0 ${SEV(f)}`} aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block font-plex-mono text-[13px] text-carbon-ink">{f.po_no || f.findingId || f.id}</span>
                      <span className="block truncate text-[12px] text-carbon-gray-60">
                        {run ? (
                          <span className="mr-2 border border-carbon-border bg-carbon-gray-10 px-1 font-plex-mono text-[10px] uppercase tracking-[0.06em] text-carbon-gray-70">run</span>
                        ) : null}
                        {run ? (f.definitionId || f.workflowRef || 'workflow') : (f.rule || 'finding')}
                        {Array.isArray(f.labels) && f.labels.length > 0 && (
                          <span className="ml-2">
                            {f.labels.map((l) => (
                              <span key={l} className="mr-1 border border-carbon-border px-1 font-plex-mono text-[10px] uppercase tracking-[0.06em] text-carbon-gray-70">{l}</span>
                            ))}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="shrink-0 font-plex-mono text-[11px] text-carbon-gray-60">
                      {f.ownerUid ? f.ownerUid : 'unassigned'}
                    </span>
                    <span className="w-[84px] shrink-0 text-right font-plex-mono text-[12px] text-carbon-ink">
                      {money(f?.evidence?.gross_ext_price)}
                    </span>
                  </button>
                  {hasActions && (
                    <div className="flex shrink-0 items-center gap-1 pr-3">
                      {showSend && (
                        <button
                          type="button"
                          onClick={() => onSend(f)}
                          disabled={busy}
                          className="border border-carbon-border px-2 py-1 font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Send
                        </button>
                      )}
                      {onAssign && (
                        <button
                          type="button"
                          onClick={() => onAssign(f)}
                          disabled={busy}
                          className="border border-carbon-border px-2 py-1 font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Assign
                        </button>
                      )}
                      {onSetLabels && (
                        <button
                          type="button"
                          onClick={() => onSetLabels(f)}
                          disabled={busy}
                          className="border border-carbon-border px-2 py-1 font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Labels
                        </button>
                      )}
                      {showReopen && (
                        <button
                          type="button"
                          onClick={() => onReopen(f)}
                          disabled={busy}
                          className="border border-carbon-border px-2 py-1 font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Reopen
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
